import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from '../../assets/logoAMK.png';
import Button from "./Button";
import { Sun, Moon, Menu, X, Home, User, Briefcase, Package, Mail, ArrowUpRight } from "lucide-react";
import { socialLinks } from "../data/socialLinks";

const Header = ({ darkMode, toggleDarkMode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const navItems = [
    { label: "Home", href: "#home", icon: <Home className="w-4 h-4 mr-3 text-indigo-500" /> },
    { label: "About", href: "#about", icon: <User className="w-4 h-4 mr-3 text-purple-500" /> },
    { label: "Projects", href: "#projects", icon: <Briefcase className="w-4 h-4 mr-3 text-sky-500" /> },
    { label: "Products", href: "#products", icon: <Package className="w-4 h-4 mr-3 text-pink-500" /> },
    { label: "Contact", href: "#contact", icon: <Mail className="w-4 h-4 mr-3 text-emerald-500" /> },
  ];

  return (
    <header className={`fixed w-full transition-all duration-300 z-50 ${
      scrolled 
        ? "bg-white/85 dark:bg-gray-950/85 backdrop-blur-xl shadow-md py-3 border-b border-gray-200/60 dark:border-gray-800/60" 
        : "bg-transparent py-5"
    }`}>
      <div className="container mx-auto px-4 sm:px-6 flex justify-between items-center">
        <a 
          href="#home" 
          className="flex items-center space-x-3 group focus:outline-none"
        >
          <div className="relative">
            <img src={Logo} alt="AMK Logo" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-indigo-500/80 group-hover:border-indigo-400 transition-all duration-300 transform group-hover:scale-105 shadow-md shadow-indigo-500/20" />
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-gray-950 animate-pulse"></span>
          </div>
          <div className="flex flex-col">
            <span className="font-heading text-base sm:text-lg font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              Matin Kadri
            </span>
            <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-widest text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <span>✦</span> Available for hire
            </span>
          </div>
        </a>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 items-center bg-gray-100/60 dark:bg-gray-900/60 backdrop-blur-md px-6 py-2 rounded-full border border-gray-200/50 dark:border-gray-800/50 shadow-inner">
          {["Home", "About", "Projects", "Contact"].map((item) => (
            <a 
              key={item} 
              href={`#${item.toLowerCase()}`} 
              className="font-medium relative py-1 px-2 group transition-colors duration-300 text-sm"
            >
              <span className="text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {item}
              </span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 group-hover:w-full rounded-full"></span>
            </a>
          ))}
        </nav>
        
        {/* Actions (Theme + CTA) */}
        <div className="hidden md:flex items-center space-x-4">
          <button
            onClick={toggleDarkMode}
            className="p-2.5 rounded-xl bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 transition-all duration-300 border border-gray-200/60 dark:border-gray-800/60 hover:scale-105 cursor-pointer shadow-sm hover:shadow-md"
            aria-label="Toggle theme"
          >
            {darkMode ? <Sun className="w-5 h-5 text-amber-400 animate-pulse" /> : <Moon className="w-5 h-5 text-indigo-600" />}
          </button>
          
          <Button 
            variant="primary" 
            href="#contact"
            className="rounded-full px-6 py-2 shadow-lg shadow-indigo-500/20 text-sm font-semibold"
          >
            Get in Touch
          </Button>
        </div>
        
        {/* Mobile Actions Container */}
        <div className="flex items-center space-x-2 sm:space-x-3 md:hidden">
          <button
            onClick={toggleDarkMode}
            className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200/60 dark:border-gray-800 transition-all cursor-pointer"
            aria-label="Toggle theme"
          >
            {darkMode ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-indigo-600" />}
          </button>
          
          <button 
            className="p-2.5 rounded-xl bg-indigo-600 text-white shadow-md focus:outline-none cursor-pointer active:scale-95 transition-transform" 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer with Framer Motion */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-gray-950/95 backdrop-blur-2xl shadow-2xl border-b border-gray-200 dark:border-gray-800 overflow-hidden"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-900">
                <span className="text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">Navigation</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/40">
                  🟢 Online
                </span>
              </div>

              {navItems.map((item) => (
                <a 
                  key={item.label}
                  href={item.href}
                  className="flex items-center justify-between text-gray-800 dark:text-gray-200 py-2.5 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900 font-semibold transition-colors text-base"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="flex items-center">
                    {item.icon}
                    {item.label}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-gray-400" />
                </a>
              ))}

              <div className="pt-2">
                <Button 
                  variant="primary" 
                  href="#contact"
                  className="w-full rounded-2xl py-3 text-center text-sm font-semibold shadow-lg shadow-indigo-500/25"
                  onClick={() => setMenuOpen(false)}
                >
                  Get in Touch
                </Button>
              </div>

              {/* Mobile Social Links Footer */}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-900 flex justify-center space-x-4">
                {socialLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200/50 dark:border-gray-800"
                    aria-label={link.name}
                  >
                    <span className="text-base">{link.icon}</span>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;